import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";

const POLL_MAX = 8;
const POLL_INTERVAL = 2000;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, status: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, status: null, error: "Missing session id" });
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= POLL_MAX) {
        setState({
          loading: false,
          status: { payment_status: "timeout" },
          error: null,
        });
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ loading: false, status: data, error: null });
          return;
        }
        if (data.status === "expired") {
          setState({ loading: false, status: data, error: null });
          return;
        }
        // pending
        setState({ loading: true, status: data, error: null });
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, status: null, error: "Could not verify payment" });
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const paid = state.status?.payment_status === "paid";
  const expired = state.status?.status === "expired";
  const timedOut = state.status?.payment_status === "timeout";

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-12 py-20" data-testid="checkout-success-page">
      <div className="brutal-card p-10 hard-shadow-red text-center">
        {state.loading && (
          <>
            <Loader2 className="animate-spin mx-auto text-[color:var(--primary)]" size={48} />
            <h1 className="font-display text-4xl mt-4">Verifying payment...</h1>
            <p className="font-body text-[color:var(--muted-text)] mt-2">
              Hang tight — this usually takes a couple seconds.
            </p>
          </>
        )}

        {!state.loading && paid && (
          <div data-testid="payment-success">
            <CheckCircle2 className="mx-auto text-[color:var(--primary)]" size={56} />
            <h1 className="font-display text-5xl mt-4">ORDER CONFIRMED.</h1>
            <p className="font-body text-[color:var(--muted-text)] mt-3">
              Thanks — your payment of <strong>${state.status.amount_total?.toFixed(2)}</strong> went through.
              We&rsquo;ve got your order queued up and will be in touch shortly.
            </p>
            <p className="label-mono text-[color:var(--muted-text)] mt-6">
              SESSION · {sessionId?.slice(0, 18)}...
            </p>
          </div>
        )}

        {!state.loading && expired && (
          <div data-testid="payment-expired">
            <XCircle className="mx-auto text-[color:var(--primary)]" size={56} />
            <h1 className="font-display text-5xl mt-4">SESSION EXPIRED.</h1>
            <p className="font-body text-[color:var(--muted-text)] mt-3">
              Your checkout session expired. No charge was made — feel free to try again.
            </p>
          </div>
        )}

        {!state.loading && timedOut && (
          <div data-testid="payment-timeout">
            <Clock className="mx-auto text-[color:var(--primary)]" size={56} />
            <h1 className="font-display text-5xl mt-4">STILL PROCESSING.</h1>
            <p className="font-body text-[color:var(--muted-text)] mt-3">
              Your payment is taking a moment. Check your email for confirmation shortly.
            </p>
          </div>
        )}

        {!state.loading && state.error && (
          <div data-testid="payment-error">
            <XCircle className="mx-auto text-[color:var(--primary)]" size={56} />
            <h1 className="font-display text-5xl mt-4">SOMETHING WENT SIDEWAYS.</h1>
            <p className="font-body text-[color:var(--muted-text)] mt-3">{state.error}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-4 justify-center mt-10">
          <Link to="/shop" className="btn-brutal btn-brutal-outline" data-testid="back-to-shop-btn">
            Back to Shop
          </Link>
          <Link to="/" className="btn-brutal btn-brutal-red" data-testid="back-home-btn">
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
}
